/**
 * SmartInboxView
 *
 * HEY/Superhuman-inspired split inbox with collapsible category sections:
 * Primary, Notifications, Feed, Paper Trail.
 */

import { useState, useCallback } from 'react';
import { ChevronDown, ChevronRight, Inbox, Bell, Newspaper, FileText, Star } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { EmailThread } from '@/lib/api';

type SmartCategory = 'primary' | 'notifications' | 'feed' | 'paper_trail';

interface SmartInboxViewProps {
  categories: Record<SmartCategory, EmailThread[]>;
  loading: boolean;
  activeThreadId: string | null;
  onClickThread: (threadId: string) => void;
  onToggleStar?: (threadId: string) => void;
}

const CATEGORY_CONFIG: {
  key: SmartCategory;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  defaultExpanded: boolean;
  description: string;
}[] = [
  {
    key: 'primary',
    label: 'Primary',
    icon: Inbox,
    defaultExpanded: true,
    description: 'Important, person-to-person emails',
  },
  {
    key: 'notifications',
    label: 'Notifications',
    icon: Bell,
    defaultExpanded: false,
    description: 'Alerts and automated notices',
  },
  {
    key: 'feed',
    label: 'Feed',
    icon: Newspaper,
    defaultExpanded: false,
    description: 'Newsletters and long-reads',
  },
  {
    key: 'paper_trail',
    label: 'Paper Trail',
    icon: FileText,
    defaultExpanded: false,
    description: 'Receipts, confirmations, transactional',
  },
];

function formatRelativeDate(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffHours < 1) return 'now';
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function ThreadRow({
  thread,
  isActive,
  isFeed,
  onClick,
  onToggleStar,
}: {
  thread: EmailThread;
  isActive: boolean;
  isFeed: boolean;
  onClick: () => void;
  onToggleStar?: () => void;
}) {
  if (isFeed) {
    // Feed style: card with content preview
    return (
      <button
        className={cn(
          'w-full text-left p-3 border border-border rounded-lg mb-2 hover:bg-muted/50 transition-colors',
          isActive && 'bg-muted border-primary/30',
          !thread.isRead && 'border-l-2 border-l-primary',
        )}
        onClick={onClick}
      >
        <div className="flex items-center justify-between mb-1">
          <span
            className={cn('text-sm', !thread.isRead ? 'font-semibold' : 'text-muted-foreground')}
          >
            {thread.subject}
          </span>
          <span className="text-xs text-muted-foreground">
            {formatRelativeDate(thread.lastMessageAt)}
          </span>
        </div>
        <p className="text-xs text-muted-foreground line-clamp-3">{thread.snippet || ''}</p>
        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
          <span>
            {thread.messageCount} message{thread.messageCount !== 1 ? 's' : ''}
          </span>
        </div>
      </button>
    );
  }

  // Standard row
  return (
    <button
      className={cn(
        'flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-muted/50 transition-colors border-b border-border',
        isActive && 'bg-muted',
        !thread.isRead && 'bg-primary/5',
      )}
      onClick={onClick}
    >
      {onToggleStar && (
        <span
          onClick={(e) => {
            e.stopPropagation();
            onToggleStar();
          }}
          className="flex-shrink-0 cursor-pointer"
        >
          <Star
            className={cn(
              'h-3.5 w-3.5',
              thread.isStarred ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground',
            )}
          />
        </span>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span
            className={cn(
              'text-sm truncate',
              !thread.isRead ? 'font-semibold' : 'text-muted-foreground',
            )}
          >
            {thread.subject}
          </span>
          <span className="text-xs text-muted-foreground flex-shrink-0">
            {formatRelativeDate(thread.lastMessageAt)}
          </span>
        </div>
        <p className="text-xs text-muted-foreground truncate">{thread.snippet || ''}</p>
      </div>
      {thread.messageCount > 1 && (
        <Badge variant="secondary" className="text-xs px-1 h-4 flex-shrink-0">
          {thread.messageCount}
        </Badge>
      )}
    </button>
  );
}

export function SmartInboxView({
  categories,
  loading,
  activeThreadId,
  onClickThread,
  onToggleStar,
}: SmartInboxViewProps) {
  const [expanded, setExpanded] = useState<Record<SmartCategory, boolean>>({
    primary: true,
    notifications: false,
    feed: false,
    paper_trail: false,
  });

  const toggleSection = useCallback((key: SmartCategory) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  if (loading) {
    return (
      <div className="p-3 space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i}>
            <Skeleton className="h-8 w-full mb-2" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ))}
      </div>
    );
  }

  const totalMessages = Object.values(categories).reduce((sum, threads) => sum + threads.length, 0);

  if (totalMessages === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-20">
        <Inbox className="h-12 w-12 mb-3 opacity-40" />
        <p className="text-sm font-medium">All caught up!</p>
        <p className="text-xs mt-1">Your inbox is empty</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {CATEGORY_CONFIG.map(({ key, label, icon: Icon, description }) => {
        const threads = categories[key];
        const isExpanded = expanded[key];
        const unreadCount = threads.filter((t) => !t.isRead).length;

        if (threads.length === 0) return null;

        return (
          <div key={key}>
            {/* Section header */}
            <button
              className="flex items-center gap-2 w-full px-3 py-2 bg-muted/50 hover:bg-muted transition-colors border-b border-border sticky top-0 z-10"
              onClick={() => toggleSection(key)}
            >
              {isExpanded ? (
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              )}
              <Icon className="h-4 w-4" />
              <span className="text-sm font-medium">{label}</span>
              <Badge variant="secondary" className="text-xs px-1.5 h-5">
                {threads.length}
              </Badge>
              {unreadCount > 0 && <Badge className="text-xs px-1.5 h-5">{unreadCount} new</Badge>}
              {!isExpanded && (
                <span className="text-xs text-muted-foreground ml-auto">{description}</span>
              )}
            </button>

            {/* Section content */}
            {isExpanded && (
              <div className={key === 'feed' ? 'p-2' : ''}>
                {threads.map((thread) => (
                  <ThreadRow
                    key={thread.id}
                    thread={thread}
                    isActive={thread.id === activeThreadId}
                    isFeed={key === 'feed'}
                    onClick={() => onClickThread(thread.id)}
                    onToggleStar={onToggleStar ? () => onToggleStar(thread.id) : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
